/**
 * Shared types for the SRE health endpoints.
 *
 * The stage numbering and ordering here always comes from the API. Earlier versions of these
 * screens kept hardcoded copies of the C# stage enums in the component files, which silently
 * drifted whenever the backend plans changed.
 */

export type SreEntityType = 'build' | 'deployment';

/**
 * Validation findings exist only on the build pipeline. `DeploymentStages` is dependency check,
 * approval, submit and track — none of them run a manifest validation, so there is nothing to
 * export. Keep this next to the type so both the API layer and the UI read the same rule.
 */
export const supportsFindings = (type: SreEntityType): boolean =>
    type === 'build';

/**
 * Mirrors the StageState enum in Azure.AirGap.Common. Used to populate the state filter; values
 * that come back on a row are read from the response rather than looked up here.
 */
export enum SreStageState {
    New = 0,
    InProgress = 1,
    Complete = 2,
    Queued = 3,
    Cancelled = 4,
    Failed = 5,
}

export const SRE_INFLIGHT_STAGE_STATES: SreStageState[] = [
    SreStageState.New,
    SreStageState.InProgress,
    SreStageState.Queued,
];

export const SRE_STAGE_STATE_NAMES: Record<number, string> = {
    [SreStageState.New]: 'New',
    [SreStageState.InProgress]: 'InProgress',
    [SreStageState.Complete]: 'Complete',
    [SreStageState.Queued]: 'Queued',
    [SreStageState.Cancelled]: 'Cancelled',
    [SreStageState.Failed]: 'Failed',
};

// ---------------------------------------------------------------------------
// Search (GET /api/{type}/health/search)
// ---------------------------------------------------------------------------

export interface SreStageCatalogEntry {
    name: string;
    /** The stage enum value, which is what the database stores. */
    number: number;
    /** One-based position within the orchestration plan. Unrelated to `number`. */
    order: number;
}

export interface SrePageInfo {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
}

export interface SreFacetBucket {
    name: string;
    number?: number | null;
    order?: number | null;
    count: number;
}

export interface SreInflightFacets {
    byStage: SreFacetBucket[];
    byStageState: SreFacetBucket[];
    byOrchestrationType: SreFacetBucket[];
}

export interface SreHealthMetric {
    orchestrationType: string;
    stage: string;
    stageNumber?: number;
    stageOrder?: number | null;
    stageState: string;
    stageStateNumber?: number;
    count: number;
    avgRetryCount: number;
    maxRetryCount: number;
}

export interface SreInflightSummary {
    /** Respects the active filter. */
    matchingCount: number;
    /** Ignores the active filter, so the UI can show "N matching of M in-flight". */
    fleetInflightCount: number;
    duplicateCount: number;
    olderThanOneDayCount: number;
    olderThanSixDaysCount: number;
}

/**
 * A single in-flight row. Build-only and deployment-only fields are optional rather than modelled
 * as a union, so list rendering does not need to narrow the type on every column.
 */
export interface SreInflightItem {
    id: number;
    uniqueIdentifier: string;
    stage: string;
    stageNumber: number;
    stageOrder: number | null;
    stageCount: number;
    stageState: string;
    stageStateNumber: number;
    retryCount: number;
    writtenTime: string;
    lastModifiedTime: string;
    orchestrationCount: number;
    orchestrationType: string;
    icmId: number;
    latestCompletedStage: string;
    latestStatusMessage: string;

    // Build only
    stagedInLowshare?: string | null;
    pipeline?: string;
    region?: string;
    artifactID?: string;

    // Deployment only
    deploymentType?: string;
}

export interface SreInflightSearchResponse {
    items: SreInflightItem[];
    metrics?: SreHealthMetric[];
    metricsScope?: 'filtered' | 'fleet';
    page: SrePageInfo;
    facets: SreInflightFacets;
    summary: SreInflightSummary;
    stageCatalog: SreStageCatalogEntry[];
    queriedAtUtc: string;
    /**
     * Set by the client, not the server, when the paged endpoint was unavailable and the response
     * was reconstructed from the legacy unpaged one.
     */
    degraded?: boolean;
    unsupportedFilters?: string[];
}

export type SreSortDirection = 'asc' | 'desc';

export interface SreInflightSearchQuery {
    uniqueIdentifier?: string;
    search?: string;
    stage?: number[];
    stageState?: number[];
    orchestrationType?: string;
    pipeline?: string;
    region?: string;
    agedOlderThanHours?: number;
    minRetryCount?: number;
    sortBy?: string;
    sortDirection?: SreSortDirection;
    page?: number;
    pageSize?: number;
}

export const SRE_DEFAULT_PAGE_SIZE = 50;
export const SRE_PAGE_SIZE_OPTIONS = [25, 50, 100];

// ---------------------------------------------------------------------------
// Progress detail (GET /api/{type}/health/details/{uniqueId})
// ---------------------------------------------------------------------------

export interface SreValidationFileGroup {
    /** Category name, for example "Changed" or "Missing". */
    name: string;
    /** Total files in this category, before the server capped the list. */
    count: number;
    files: string[];
    truncated: boolean;
}

export interface SreHashingFailure {
    file: string;
    reason: string;
}

/**
 * Per-file validation findings for a stage.
 *
 * Recovered server-side from the stage status message, which the validator already writes into SQL.
 * Blob-transfer entries are content-addressed hashes; manifest-validation entries are paths.
 */
export interface SreValidationDetail {
    groups: SreValidationFileGroup[];
    hashingFailures: SreHashingFailure[];
    hashingFailureCount?: number;
    totalFindings: number;
    truncated: boolean;
}

/**
 * One validation finding, flattened for export.
 *
 * `entry` is a reported file path or Build/<sha256> for content-addressed blob findings.
 * Older API responses may contain bare hashes; the UI normalizes them. `isHash` says which.
 */
export interface SreValidationFindingRow {
    uniqueIdentifier: string;
    pipeline: string;
    region: string;
    artifactID: string;
    orchestrationType: string;
    stageNumber: number;
    stage: string;
    category: string;
    entry: string;
    isHash: boolean;
    reason: string;
}

export interface SreValidationFindingsResponse {
    rows: SreValidationFindingRow[];
    matchedOrchestrations: number;
    matchedBuilds?: number;
    nextContinuationToken?: string | null;
    orchestrationsWithFindings: number;
    truncated: boolean;
    rowLimit: number;
    queriedAtUtc: string;
}

export interface SreProgressStage {
    stage: string;
    successful: boolean;
    completed: boolean;
    statusMessage: string;
    writtenTime: string;
    lastModifiedTime: string;
    retryCount: number;
    orchestrationCount: number;
    executionDurationSeconds: number;
    /** Time between the previous stage finishing and this one starting. */
    pickupDurationSeconds?: number | null;

    // Added by the newer API; absent when talking to an older SRE deployment.
    stageNumber?: number;
    stageOrder?: number | null;
    stageCount?: number;
    validationDetail?: SreValidationDetail | null;
}

/**
 * Shared by the legacy SRE cards, which each declared their own identical copy.
 */
export interface BuildHealthDetail {
    id: number;
    uniqueIdentifier: string;
    stage: string;
    stageState: string;
    retryCount: number;
    lastModifiedTime: string;
    writtenTime: string;
    orchestrationCount: number;
    stagedInLowshare?: string | null;
    pipeline: string;
    region: string;
    artifactID: string;
    latestCompletedStage: string;
    latestStatusMessage: string;
}

export interface DeploymentHealthDetail {
    id: number;
    uniqueIdentifier: string;
    stage: string;
    stageState: string;
    retryCount: number;
    lastModifiedTime: string;
    writtenTime: string;
    orchestrationCount: number;
    stagedInLowshare?: string | null;
    deploymentType: string;
    latestCompletedStage: string;
    latestStatusMessage: string;
}

export type SreHealthDetail = BuildHealthDetail | DeploymentHealthDetail;

/**
 * Recorded artifact locations and configured transfer routing, not live blob existence checks.
 * Legacy scalar account/container fields describe an uploaded artifact, not a transfer candidate.
 * `flowKey` identifies the configured route even when its flow id is unavailable.
 */
export interface SreTransferInfo {
    storageAccount?: string;
    storageContainer?: string;
    workLane?: string;
    flowKey?: string;
    flow?: string;
    flowId?: string | null;
    transferLocations?: SreStorageLocation[];
    artifactLocations?: SreStorageLocation[];
}

export interface SreStorageLocation {
    storageAccount: string;
    storageContainer: string;
    uri: string;
    source: string;
    blobPrefix: string;
    preferred: boolean;
}

export interface SreProgressResponse {
    healthDetail: SreHealthDetail;
    orchestrationType?: string;
    icmId?: number;
    stages: SreProgressStage[];
    hasErrors: boolean;
    totalElapsedSeconds: number;
    /** Added by the newer API; absent when talking to an older SRE deployment. */
    stageCatalog?: SreStageCatalogEntry[];
    /** Added by the newer API; absent when talking to an older SRE deployment. */
    transferInfo?: SreTransferInfo;
}

// ---------------------------------------------------------------------------
// Legacy unpaged health snapshot (GET /api/{type}/health) - fallback path only
// ---------------------------------------------------------------------------

export interface StageMetric {
    stage: string;
    stageState: string;
    count: number;
    avgRetryCount: number;
    maxRetryCount: number;
}

export interface SreLegacyHealthResponse {
    inflight: { [orchestrationType: string]: StageMetric[] };
    details: SreHealthDetail[];
    duplicateOrchestrations: string[];
    olderThanOneDay: string[];
    olderThanSixDays: string[];
}
